'use client';

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import VBox from '../../components/layout/VBox';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import CampoTexto from '../../components/Formulario/CampoTexto';
import AlertBox from '@/components/layout/AlertBox';
import { AnimatedBackground } from '../login/bg/AnimatedBackground';
import useWindowSize from '../../lib/useWindowSize';
import { Link } from '@/components/layout/Link';
import styles from './setup.module.css';
import { mdiCheck, mdiClose, mdiLoading, mdiClockOutline, mdiPartyPopper, mdiAlertCircle, mdiCheckCircle } from '@mdi/js';
import Icon from '@/components/Icon';

type SetupState =
    | 'loading'
    | 'expired'
    | 'used'
    | 'invalid'
    | 'form'
    | 'submitting'
    | 'success'
    | 'error';

const SetupPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [width, height, mobile] = useWindowSize();
    const [pause] = useState(false); // Background não pausável nesta página

    const token = searchParams.get('token');

    // Estado da página
    const [state, setState] = useState<SetupState>('loading');
    const [email, setEmail] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');

    // Campos do formulário
    const [subdomain, setSubdomain] = useState('');

    // Verificação de disponibilidade
    const [checkingAvailability, setCheckingAvailability] = useState(false);
    const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
    const [availabilityMessage, setAvailabilityMessage] = useState('');

    // Reenvio de convite
    const [resendCountdown, setResendCountdown] = useState(0);
    const [resending, setResending] = useState(false);

    // Sucesso
    const [tenantKey, setTenantKey] = useState('');
    const [redirectUrl, setRedirectUrl] = useState('');

    // Validar token ao carregar
    useEffect(() => {
        if (!token) {
            setState('invalid');
            setErrorMessage('Token não fornecido. Use o link enviado por email.');
            return;
        }

        validateToken();
    }, [token]);

    const validateToken = async () => {
        try {
            const response = await axios.get(`/api/setup/validate/${token}`);
            if (response.data.ok) {
                setEmail(response.data.email);
                setState('form');
            }
        } catch (error: any) {
            if (axios.isAxiosError(error) && error.response) {
                const { expired, used, email: inviteEmail } = error.response.data;
                setEmail(inviteEmail || '');

                if (expired) {
                    setState('expired');
                } else if (used) {
                    setState('used');
                } else {
                    setState('invalid');
                    setErrorMessage(error.response.data.msg || 'Token inválido');
                }
            } else {
                setState('error');
                setErrorMessage('Erro ao validar token. Tente novamente mais tarde.');
            }
        }
    };

    // Verificar disponibilidade do subdomínio com debounce
    useEffect(() => {
        if (!subdomain || subdomain.length < 3) {
            setIsAvailable(null);
            setAvailabilityMessage('');
            return;
        }

        const timeoutId = setTimeout(() => {
            checkSubdomainAvailability(subdomain);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [subdomain]);

    const checkSubdomainAvailability = async (value: string) => {
        setCheckingAvailability(true);
        try {
            const response = await axios.get(`/api/setup/check-subdomain/${value.toLowerCase()}`);
            setIsAvailable(response.data.available);
            setAvailabilityMessage(response.data.msg);
        } catch (error: any) {
            if (axios.isAxiosError(error) && error.response) {
                setIsAvailable(false);
                setAvailabilityMessage(error.response.data.msg || 'Subdomínio inválido');
            }
        } finally {
            setCheckingAvailability(false);
        }
    };

    // Submeter formulário
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isAvailable) {
            setErrorMessage('Por favor, escolha um subdomínio disponível.');
            return;
        }

        setState('submitting');
        setErrorMessage('');

        try {
            const response = await axios.post('/api/setup/complete', {
                token,
                subdomain: subdomain.toLowerCase(),
            });

            if (response.data.ok) {
                setTenantKey(response.data.tenantKey);
                setRedirectUrl(response.data.redirectUrl);
                setState('success');
            }
        } catch (error: any) {
            setState('form');
            if (axios.isAxiosError(error) && error.response) {
                if (error.response.status === 410) {
                    setState('expired');
                } else {
                    setErrorMessage(error.response.data.msg || 'Erro ao criar conta');
                }
            } else {
                setErrorMessage('Erro ao criar conta. Tente novamente mais tarde.');
            }
        }
    };

    // Reenviar convite
    const handleResend = async () => {
        if (resendCountdown > 0 || !email) return;

        setResending(true);
        try {
            const response = await axios.post('/api/setup/resend', { email });

            if (response.data.ok) {
                // Iniciar countdown de 2 minutos
                setResendCountdown(120);
                setErrorMessage('');
            }
        } catch (error: any) {
            if (axios.isAxiosError(error) && error.response) {
                if (error.response.status === 429) {
                    setResendCountdown(error.response.data.waitMinutes * 60);
                } else {
                    setErrorMessage(error.response.data.msg || 'Erro ao reenviar convite');
                }
            }
        } finally {
            setResending(false);
        }
    };

    // Countdown timer
    useEffect(() => {
        if (resendCountdown <= 0) return;

        const timer = setInterval(() => {
            setResendCountdown(prev => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [resendCountdown]);

    const formatCountdown = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Renderizar conteúdo baseado no estado
    const renderContent = () => {
        switch (state) {
            case 'loading':
                return (
                    <div className={styles.loadingSpinner}>
                        <Icon path={mdiLoading} size={2} spin color="var(--tc-color-primary)" />
                    </div>
                );

            case 'expired':
                return (
                    <div className={styles.expiredContainer}>
                        <div className={styles.expiredIcon}>⏰</div>
                        <h2 className={styles.title}>Link Expirado</h2>
                        <p className={styles.subtitle}>
                            O link de convite para <span className={styles.email}>{email}</span> expirou.
                        </p>
                        <VBox gap={16}>
                            <Botao
                                variant="primary"
                                onClick={handleResend}
                                disabled={resendCountdown > 0 || resending}
                                style={{ width: '100%' }}
                            >
                                {resending ? 'Enviando...' : 'Reenviar Convite'}
                            </Botao>

                            {resendCountdown > 0 && (
                                <div className={styles.resendCounter}>
                                    <Icon path={mdiClockOutline} size={0.8} />
                                    <span>Aguarde {formatCountdown(resendCountdown)} para reenviar</span>
                                </div>
                            )}

                            {errorMessage && <AlertBox variant="danger">{errorMessage}</AlertBox>}
                        </VBox>
                    </div>
                );

            case 'used':
                return (
                    <div className={styles.expiredContainer}>
                        <div className={styles.expiredIcon}>
                            <Icon path={mdiCheckCircle} size={3} color="var(--tc-color-success)" />
                        </div>
                        <h2 className={styles.title}>Convite Já Utilizado</h2>
                        <p className={styles.subtitle}>
                            Este convite já foi utilizado para criar uma conta.
                        </p>
                    </div>
                );

            case 'invalid':
            case 'error':
                return (
                    <div className={styles.expiredContainer}>
                        <div className={styles.expiredIcon}>
                            <Icon path={mdiAlertCircle} size={2} color="var(--tc-color-danger)" />
                        </div>
                        <h2 className={styles.title}>Erro</h2>
                        <p className={styles.subtitle}>{errorMessage}</p>
                        <Botao
                            variant="secondary"
                            onClick={() => navigate('/login')}
                            style={{ width: '100%' }}
                        >
                            Voltar
                        </Botao>
                    </div>
                );

            case 'form':
            case 'submitting':
                return (
                    <VBox>
                        <h2 className={styles.title}>Configure sua Conta</h2>
                        <p className={styles.subtitle}>
                            Configurando para <span className={styles.email}>{email}</span>
                        </p>

                        <form onSubmit={handleSubmit}>
                            <VBox gap={20}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: 'var(--tc-color-gray-700)' }}>
                                        Seu Domínio *
                                    </label>
                                    <div className={styles.domainInput}>
                                        <CampoTexto
                                            name="subdomain"
                                            placeholder="minha-empresa"
                                            value={subdomain}
                                            onChange={(value) => setSubdomain(value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                            required
                                            disabled={state === 'submitting'}
                                            autoFocus
                                            append={<span>{import.meta.env.PROD ? '.terachat.app' : '.local:3100'}</span>}
                                        />
                                    </div>

                                    {subdomain.length >= 3 && (
                                        <div className={`${styles.availabilityCheck} ${checkingAvailability ? styles.checking :
                                            isAvailable ? styles.available : styles.unavailable
                                            }`}>
                                            {checkingAvailability ? (
                                                <>
                                                    <Icon path={mdiLoading} size={0.7} spin />
                                                    <span>Verificando disponibilidade...</span>
                                                </>
                                            ) : isAvailable ? (
                                                <>
                                                    <Icon path={mdiCheck} size={0.7} />
                                                    <span>{availabilityMessage}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Icon path={mdiClose} size={0.7} />
                                                    <span>{availabilityMessage}</span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {errorMessage && <AlertBox variant="danger">{errorMessage}</AlertBox>}

                                <Botao
                                    type="submit"
                                    variant="primary"
                                    style={{ width: '100%' }}
                                    disabled={state === 'submitting' || !isAvailable || checkingAvailability}
                                >
                                    {state === 'submitting' ? 'Criando sua conta...' : 'Criar Minha Conta'}
                                </Botao>

                                <p style={{ fontSize: '0.875rem', color: 'var(--tc-color-gray-600)', textAlign: 'center', margin: 0 }}>
                                    Ao continuar, você aceita nossos{' '}
                                    <Link href="/termos-uso" target="_blank">Termos de Uso</Link>
                                    {' '}e{' '}
                                    <Link href="/politica-privacidade" target="_blank">Política de Privacidade</Link>
                                </p>
                            </VBox>
                        </form>
                    </VBox>
                );

            case 'success':
                return (
                    <div className={styles.successContainer}>
                        <h2 className={styles.title}>Parabéns! 🎉</h2>
                        <p className={styles.subtitle}>Sua conta foi criada com sucesso!</p>

                        <div className={styles.successDomain}>{tenantKey}</div>

                        <Botao
                            variant="primary"
                            onClick={() => window.location.href = redirectUrl}
                            style={{ width: '100%' }}
                        >
                            Acessar Minha Conta
                        </Botao>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className={styles.setupContainer}>
            <AnimatedBackground width={width} height={height} pause={pause} />

            {!mobile && (
                <img
                    src="/logo-terachat-branco.png"
                    alt="Logo TeraChat"
                    className={styles.logo}
                />
            )}

            <div>
                <div className={`${styles.card} ${mobile ? styles.cardMobile : ''}`}>
                    {mobile && (
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <img
                                src="/logo-terachat-branco.png"
                                alt="Logo TeraChat"
                                className={styles.logoMobile}
                            />
                        </div>
                    )}

                    {renderContent()}
                </div>

                <div className={styles.footer}>
                    Made with ❤️ by <Link href="https://terachat.com.br" target="_blank">Tera Sistemas</Link>
                </div>
            </div>
        </div>
    );
};

export default SetupPage;
